// src/components/Auth/Register.js
import React, { useState } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import api from '../../services/api';
import {
  Container,
  Card,
  CardContent,
  Typography,
  Stack,
  TextField,
  Button,
  ToggleButton,
  ToggleButtonGroup,
  Box,
  FormControlLabel,
  Checkbox,
  InputAdornment,
  IconButton,
  CircularProgress,
  Alert,
  Link
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { toast } from 'react-toastify';
import API from '../config';

export default function Register() {
  const navigate = useNavigate();

  // Mode toggle
  const [mode, setMode] = useState('verified'); // 'default' or 'verified'

  // Form state
  const [form, setForm] = useState({
    email: '',
    username: '',
    password: '',
    birthdate: '',
    isCreator: false,
  });

  // OTP state
  const [emailSent, setEmailSent]       = useState(false);
  const [otpCode, setOtpCode]           = useState('');
  const [isVerified, setIsVerified]     = useState(false);
  const [sendingOtp, setSendingOtp]     = useState(false);
  const [verifyingOtp, setVerifyingOtp] = useState(false);

  // Other UI state
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading]           = useState(false);
  const [error, setError]               = useState('');

  const handleModeChange = (_, value) => {
    if (value) setMode(value);
  };

  const handleChange = e => {
    const { name, value, checked, type } = e.target;
    setForm(f => ({
      ...f,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const toggleShowPassword = () => setShowPassword(p => !p);

  const sendOtp = async () => {
    if (!form.email) {
      toast.error('Please enter your email first');
      return;
    }
    setSendingOtp(true);
    try {
      await api.post('/api/auth/send-otp', { email: form.email });
      toast.info('Verification code sent to your email');
      setEmailSent(true);
    } catch (error) {
      console.error('Send OTP error:', error);
      toast.error('Failed to send code');
    } finally {
      setSendingOtp(false);
    }
  };

  const verifyOtp = async () => {
    if (!otpCode) {
      toast.error('Enter the code you received');
      return;
    }
    setVerifyingOtp(true);
    try {
      await api.post('/api/auth/verify-otp', {
        email: form.email,
        code: otpCode
      });
      toast.success('Email verified!');
      setIsVerified(true);
    } catch (err) {
      console.error('Verify OTP error:', err);
      toast.error(err.response?.data?.error || 'Invalid or expired code');
    } finally {
      setVerifyingOtp(false);
    }
  };

  const handleRegister = async e => {
    e.preventDefault();
    if (!isVerified) {
      toast.error('Please verify your email first.');
      return;
    }
    setLoading(true);
    const referredBy = localStorage.getItem('referredBy') || null;
    const endpoint = mode === 'default' ? '/api/auth/register' : '/api/auth/verified-register';

    try {
      await api.post(endpoint, { ...form, referredBy });
      toast.success('Registration successful! Check your email.');
      setTimeout(() => navigate('/login'), 1500);
    } catch (err) {
      console.error('Registration error:', err);
      toast.error(err.response?.data?.error || 'Registration failed.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="xs" sx={{ mt: 8, mb: 4 }}>
      <Card elevation={3}>
        <CardContent>
          <Typography variant="h5" align="center" gutterBottom>
            {mode === 'default' ? 'Register' : 'Verified Registration'}
          </Typography>

          {/* Only for verified mode: show disclaimer */}
          {mode === 'verified' && (
            <Typography
              variant="body2"
              align="center"
              color="text.secondary"
              sx={{ mb: 2, px: 1 }}
            >
              Must use the same credentials as your OnlyFans account. We encrypt your information—
              we never access or store your OF password.
            </Typography>
          )}

          {/* Mode toggle */}
          <Box textAlign="center" sx={{ mb: 2 }}>
            <ToggleButtonGroup
              value={mode}
              exclusive
              onChange={handleModeChange}
              size="small"
            >
              <ToggleButton value="default">Standard</ToggleButton>
              <ToggleButton value="verified">Verified</ToggleButton>
            </ToggleButtonGroup>
          </Box>

          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

          <Stack spacing={2}>
            {/* Email input */}
            <TextField
              label="Email"
              name="email"
              type="email"
              value={form.email}
              onChange={handleChange}
              required
              fullWidth
              disabled={emailSent}
            />

            {/* Send or verify OTP */}
            {!emailSent ? (
              <Button
                variant="outlined"
                onClick={sendOtp}
                disabled={sendingOtp}
                fullWidth
              >
                {sendingOtp ? <CircularProgress size={20} /> : 'Send Verification Code'}
              </Button>
            ) : (
              <Stack direction="row" spacing={1}>
                <TextField
                  label="OTP Code"
                  value={otpCode}
                  onChange={e => setOtpCode(e.target.value)}
                  fullWidth
                  disabled={isVerified}
                />
                <Button
                  variant="contained"
                  onClick={verifyOtp}
                  disabled={verifyingOtp || isVerified}
                >
                  {verifyingOtp
                    ? <CircularProgress size={20} color="inherit" />
                    : (isVerified ? 'Verified ✓' : 'Verify')}
                </Button>
              </Stack>
            )}

            {/* Once verified, show rest of form */}
            {emailSent && isVerified && (
              <Box component="form" onSubmit={handleRegister}>
                <Stack spacing={2}>

                  <TextField
                    label="Username"
                    name="username"
                    value={form.username}
                    onChange={handleChange}
                    required
                    fullWidth
                  />

                  <TextField
                    label="Password"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    value={form.password}
                    onChange={handleChange}
                    required
                    fullWidth
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton onClick={toggleShowPassword} edge="end">
                            {showPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      )
                    }}
                  />

                  <TextField
                    label="Birthdate"
                    name="birthdate"
                    type="date"
                    value={form.birthdate}
                    onChange={handleChange}
                    required
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                  />

                  <FormControlLabel
                    control={
                      <Checkbox
                        name="isCreator"
                        checked={form.isCreator}
                        onChange={handleChange}
                      />
                    }
                    label="I am a creator"
                  />

                  <Button
                    type="submit"
                    variant="contained"
                    fullWidth
                    disabled={loading}
                  >
                    {loading ? <CircularProgress size={20} color="inherit" /> : 'Register'}
                  </Button>
                </Stack>
              </Box>
            )}
          </Stack>

          <Typography variant="body2" align="center" sx={{ mt: 2 }}>
            Already have an account?{' '}
            <Link component={RouterLink} to="/login">
              Log in
            </Link>
          </Typography>
        </CardContent>
      </Card>
    </Container>
  );
}
